import React, { useCallback, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { di } from 'react-magnetic-di';

import { useFlags } from '@atlaskit/flag/use-flags';
import { FetchError } from '@atlassian/help-center-common-util/fetch/fetch';
import type { AsyncFieldConfiguration } from '@atlassian/proforma-form-renderer/async-fields';
import type { FieldValues } from '@atlassian/proforma-form-renderer/field-values';
import type { ProformaForm } from '@atlassian/proforma-form-renderer/proforma-form';

import { useCmdbObjectsService } from '../../../../../services/cmdb-objects';
import type { CmdbAutocompleteResponse } from '../../../../../services/cmdb-objects/types';

import { CmdbIcon } from './cmdb-icon';
import { messages } from './messages';
import { createAttributeString, getVisibleAttributes } from './utils';

export const useCmdbObjects = () => {
	di(useCmdbObjectsService, useFlags);
	const { error, fetch } = useCmdbObjectsService();
	const { showFlag } = useFlags();

	useEffect(() => {
		if (error instanceof FetchError && error.status === 400) {
			showFlag({
				description: <FormattedMessage {...messages.searchErrorDescription} />,
				isAutoDismiss: true,
				title: <FormattedMessage {...messages.searchErrorTitle} />,
				appearance: 'error',
			});
		}
	}, [error, showFlag]);

	return useCallback(
		(
			jiraFieldKey: string,
			filterAttributes: string[],
			proformaForm: ProformaForm,
		): AsyncFieldConfiguration => {
			// TODO: REQIN-4790 — Replace displayLabel with an optionComponent in AsyncFieldConfiguration
			// to keep CMDB rendering concerns out of the generic platform FormOptionsType type.
			// Design doc: https://hello.atlassian.net/wiki/spaces/FORMS/pages/7117590146
			const mapChoices = (choices: CmdbAutocompleteResponse) =>
				choices.results.map((choice) => {
					const visibleAttributes = getVisibleAttributes(choice.attributes, filterAttributes);
					const [primaryAttribute, ...secondaryAttributes] = visibleAttributes;
					const secondaryAttributesDescription = createAttributeString(secondaryAttributes);
					return {
						label: choice.label,
						value: choice.id,
						avatar: <CmdbIcon {...choice.avatar.mediaClientConfig} iconUrl={choice.avatar.url48} />,
						byline: secondaryAttributesDescription,
						displayLabel: primaryAttribute
							? createAttributeString([primaryAttribute])
							: choice.label,
					};
				});

			return {
				// TODO: Update useCmdbDefaults to use loadPaginatedOptions, then remove loadOptions.
				loadOptions: async (query, currentValues) => {
					return fetch({
						currentValues,
						jiraFieldKey,
						proformaForm,
						query,
						start: 0,
					}).then(mapChoices);
				},
				loadPaginatedOptions: async (
					query: string,
					currentValues: FieldValues,
					_fieldId: string,
					start: number,
				) => {
					const choices = await fetch({
						currentValues,
						jiraFieldKey,
						proformaForm,
						query,
						start,
					});
					return {
						options: mapChoices(choices),
						hasNextPage: choices.hasNextPage,
					};
				},
			};
		},
		[fetch],
	);
};
